import { useEffect, useRef } from "react";

const VINE = "#B596D4";
const BLOOM = "#E6A3C6";
// small five-petal blossoms placed along the vines, in % of the frame
const BLOSSOMS = [[2, 6], [30, 1.5], [71, 2.5], [97, 9], [99, 52], [96, 92], [62, 98.5], [24, 97.5], [1.5, 88], [0.5, 44]];

function Blossom({ x, y, r, i }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${i * 37})`}>
      {[0, 72, 144, 216, 288].map((a) => (
        <ellipse key={a} cx="0" cy={-r} rx={r * 0.55} ry={r} fill={BLOOM} transform={`rotate(${a})`} />
      ))}
      <circle r={r * 0.35} fill="#F3D27E" />
    </g>
  );
}

// A faint watercolour vine border around the poem; bubbles drifting near ripple it, and a pop breaks it apart before it reforms.
export function PoemBorder() {
  const disp = useRef();
  const group = useRef();
  const box = useRef();
  useEffect(() => {
    let d = 0, raf, last = performance.now();
    const onPop = () => (d = 1);
    window.addEventListener("afae-bubble-pop", onPop);
    const near = setInterval(() => {
      const r = box.current?.getBoundingClientRect();
      const list = window.__afaeBubbles?.info?.().bubbles || [];
      if (!r) return;
      for (const b of list) {
        const dx = Math.max(r.left - b.x, 0, b.x - r.right), dy = Math.max(r.top - b.y, 0, b.y - r.bottom);
        const dist = Math.hypot(dx, dy) - (b.r || 0);
        if (dist < 60) d = Math.max(d, 0.45 * (1 - Math.max(0, dist) / 60));
      }
    }, 200);
    const tick = (now) => {
      raf = requestAnimationFrame(tick);
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      d *= Math.exp(-dt * 1.6);
      disp.current?.setAttribute("scale", (1.5 + 24 * d).toFixed(2));
      if (group.current) group.current.style.opacity = (1 - 0.75 * Math.max(0, d - 0.35) / 0.65).toFixed(3);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      clearInterval(near);
      window.removeEventListener("afae-bubble-pop", onPop);
    };
  }, []);
  return (
    <div ref={box} data-testid="poem-border" aria-hidden className="poem-border-cycle pointer-events-none absolute -inset-x-4 -inset-y-3 sm:-inset-x-7 sm:-inset-y-4">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="h-full w-full overflow-visible">
        <defs>
          <filter id="poem-wash" x="-10%" y="-10%" width="120%" height="120%">
            <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="4" result="n" />
            <feDisplacementMap ref={disp} in="SourceGraphic" in2="n" scale="1.5" xChannelSelector="R" yChannelSelector="G" result="d" />
            <feGaussianBlur in="d" stdDeviation="0.25" />
          </filter>
        </defs>
        <g ref={group} filter="url(#poem-wash)" fill="none" stroke={VINE} strokeLinecap="round" vectorEffect="non-scaling-stroke">
          <path d="M3 10 C 12 2, 22 5, 34 2.5 S 58 4, 70 2 S 90 3, 97 9" strokeWidth="1.1" vectorEffect="non-scaling-stroke" />
          <path d="M97 12 C 99.5 26, 97.5 38, 99 52 S 97 76, 96.5 90" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          <path d="M95 94 C 84 97, 74 99.5, 62 98 S 36 96.5, 24 98 S 8 97, 2 90" strokeWidth="1.1" vectorEffect="non-scaling-stroke" />
          <path d="M1.5 86 C 0 72, 2 58, 0.5 44 S 2 20, 3 12" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          {[[14, 3.6, -1], [48, 3.2, 1], [84, 2.6, -1], [44, 97.4, 1], [10, 97.6, -1], [98.2, 30, 1], [97.6, 72, -1], [1, 64, 1], [1.4, 24, -1]].map(([x, y, s], i) => (
            <path key={i} d={`M${x} ${y} q ${2.5 * s} ${-1.8} ${4.5 * s} ${-0.4}`} strokeWidth="0.8" vectorEffect="non-scaling-stroke" />
          ))}
          <g stroke="none">
            {BLOSSOMS.map(([x, y], i) => (
              <Blossom key={i} x={x} y={y} r={0.9 + (i % 3) * 0.25} i={i} />
            ))}
          </g>
        </g>
      </svg>
    </div>
  );
}
